/**
 * Unified checkout dispatcher.
 *
 * Routes checkout to Paddle, the sole payment provider.
 */

import { apiV2 } from './api-v2-client'
import { openPaddleCheckout, type CheckoutOptions } from './paddle'
import type { BillingInterval, PaidPlan } from './plans'

/**
 * Opens the Paddle checkout.
 *
 * @param _provider - Unused (kept for API compatibility)
 * @param _checkoutUrl - Unused (kept for API compatibility)
 * @param transactionId - Transaction/subscription ID
 * @param successUrl - Redirect URL after successful payment
 * @param opts - Optional overlay callbacks
 */
export async function openCheckout(
  _provider: string | undefined,
  _checkoutUrl: string,
  transactionId: string,
  successUrl: string,
  opts?: CheckoutOptions,
) {
  await openPaddleCheckout(transactionId, successUrl, opts)
}

/**
 * Create a transaction for `plan`/`interval` and open the overlay.
 * `promoCode` goes to the server only; it is never in the success URL or in the funnel event.
 */
export async function startCheckout(
  plan: PaidPlan,
  interval: BillingInterval,
  opts?: CheckoutOptions & { promoCode?: string },
) {
  const { promoCode, ...overlay } = opts ?? {}
  const result = await apiV2.createCheckout(plan, interval, promoCode)
  const successUrl = `${window.location.origin}/dashboard/settings?billing=success&plan=${plan}`
  await openCheckout(result.provider, result.checkout_url, result.transaction_id, successUrl, overlay)
  // Funnel step; tracking must never block or fail checkout.
  apiV2.postFunnelEvent('checkout_opened', { plan, interval }).catch(() => {})
}

export function getPaymentProvider(): string {
  return 'paddle'
}
